/**
 * Backend persistence for the guided tour. State lives on the member row
 * (per workspace): `tourStatus` drives the auto-shown welcome tour, and
 * `tourProgress` ({ [trackId]: status }) records the opt-in feature tracks
 * replayed from the Help center. Read on workspace selection / Help mount,
 * written when a tour is completed or dismissed.
 */
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';

/**
 * Full tour state for the member in this workspace.
 * @param {number|string} workspaceId
 * @returns {Promise<{tourStatus: string, tourProgress: object}|null>} null on failure
 */
export async function getTourState(workspaceId) {
  if (!workspaceId) return null;
  const res = await sendRequest({ method: 'get', url: API.WORKSPACE.tourStatus(workspaceId) });
  if (res.success) {
    return {
      tourStatus: res.data?.tourStatus || 'pending',
      tourProgress: res.data?.tourProgress || {},
    };
  }
  return null;
}

/**
 * Welcome-tour status only (used by the auto-show check).
 * @param {number|string} workspaceId
 * @returns {Promise<string|null>} 'pending' | 'completed' | 'dismissed', or null on failure
 */
export async function getTourStatus(workspaceId) {
  const state = await getTourState(workspaceId);
  return state ? state.tourStatus : null;
}

/**
 * @param {number|string} workspaceId
 * @param {string} status 'completed' | 'dismissed'
 * @param {string} [tour] feature track id; omit (or 'welcome') for the welcome tour
 * @returns {Promise<boolean>} whether the write succeeded
 */
export async function setTourStatus(workspaceId, status, tour) {
  if (!workspaceId) return false;
  const data = { status };
  if (tour && tour !== 'welcome') data.tour = tour;
  const res = await sendRequest({
    method: 'put',
    url: API.WORKSPACE.tourStatus(workspaceId),
    data,
  });
  return !!res.success;
}
